/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */
import './style/importStatus.scss';

import React, {useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {AwesomeIcon, CustomAppEvent, WithEvents} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import {AccountStatus} from '../../util/interfaces';

import classNames from 'classnames';

import {getCurrentError, getCurrentStatus} from '../account/pinger/statusPinger';

import {AppContext} from '../misc/appContext';

import InfoBanner from '../misc/infoBanner';

import {IconName} from '@fortawesome/fontawesome-svg-core';

const now = IDate.now();

export default function ImportStatus() {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const [status, setStatus] = useState(
        `${t('dashboard.importStatus.updated', 'Bijgewerkt')}, ${t(
            'dashboard.importStatus.lastCheck',
            'laatste controle'
        )} ${now.format('HH:mm')}`
    );
    const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(AccountStatus.IDLE);
    const event: CustomAppEvent = {eventName: 'onPingStatusEvent', handler: onPingStatusEvent};
    const showImportInfo = context.userContext.user?.getFlex('import_info_shown') !== true;
    const statusIcon = useMemo(() => {
        let iconName: IconName;
        if (accountStatus === AccountStatus.IDLE) iconName = 'check';
        else if (accountStatus === AccountStatus.IMPORTING) iconName = 'sync-alt';
        else if (accountStatus === AccountStatus.ERROR) iconName = 'exclamation-circle';
        else iconName = 'ban';
        return <AwesomeIcon name={iconName} className='status-icon' />;
    }, [accountStatus]);

    function onPingStatusEvent() {
        const status: AccountStatus | null = getCurrentStatus(),
            error: string = getCurrentError();
        if (status) setData(status, error);
        else setAccountStatus(null);
    }

    function setData(status: AccountStatus, error: string) {
        setAccountStatus(status);
        const dateLabel = `, ${t('dashboard.importStatus.lastCheck', 'laatste controle')} ${IDate.now().format(
            'HH:mm'
        )}`;
        let nextStatus: string;
        if (status === AccountStatus.ERROR)
            nextStatus = error || t('dashboard.importStatus.error', 'Probleem bij controleren op nieuwe orders');
        else if (status === AccountStatus.IDLE)
            nextStatus = t('dashboard.importStatus.updated', 'Bijgewerkt') + dateLabel;
        else if (status === AccountStatus.IMPORTING)
            nextStatus = t('dashboard.importStatus.importing', 'Controleren op nieuwe orders') + dateLabel;
        else nextStatus = t('dashboard.importStatus.notConnected', 'Niet verbonden met Bol');
        setStatus(nextStatus);
    }

    useEffect(() => {
        onPingStatusEvent();
    }, [context.userContext.user]);

    const showSearchTipBanner = accountStatus === AccountStatus.IDLE && showImportInfo;
    return (
        <WithEvents events={event}>
            {accountStatus === null ? null : showSearchTipBanner ? (
                <InfoBanner
                    variant='warning'
                    persistDismiss
                    className='import-status-banner'
                    featureKey='search_invoices'
                >
                    {t(
                        'dashboard.importStatus.searchTip',
                        'Via de zoekbalk kun je óók orders ophalen via het ordernummer die van vóór het begin gebruik van Bluemate zijn, tot wel 2 jaar terug.'
                    )}
                </InfoBanner>
            ) : accountStatus === AccountStatus.IDLE ? null : (
                // PWA-06: the reassuring green "Bijgewerkt, laatste controle HH:mm" pill is
                // hidden — it is always up-to-date and adds no signal. The problem states
                // (ERROR / IMPORTING / not-connected) and the new-user searchTip banner stay.
                <div className={classNames('status-row', accountStatus)}>
                    {statusIcon}
                    <label className='status-label'>{status}</label>
                </div>
            )}
        </WithEvents>
    );
}
